// The only client error API (ADR 003; PostHog client spec §3/§4 step 7).
// Every client file reports through here; only this module and src/index.js
// (Sentry.init) import @sentry/react -- src/__tests__/sentry-ownership.guard.test.js
// enforces it.
//
// Each export forwards the caller's arguments to Sentry unchanged -- no default
// level, no added context -- so Sentry severity and grouping are exactly what the
// call site wrote. Sentry runs first; then the PostHog half (PR B, spec §4 steps
// 7-9) sends only coded tags (fn / stage / boundary), never ctx.extra or message
// text, capped per error key per app process, inside a try/catch that swallows.
import * as Sentry from '@sentry/react';
import { captureError, track } from './analytics';
import { TAG_RE } from './analytics-events';

// C4: on iOS one process can live for days; an error loop (e.g. a Realtime storm)
// sends at most this many PostHog events per key. Sentry stays uncapped.
const POSTHOG_CAP_PER_KEY = 3;
const capCounts = new Map();

function codedProps(ctx) {
  const tags = ctx?.tags;
  const props = {};
  const keep = (key, value) => {
    if (typeof value === 'string' && TAG_RE.test(value)) props[key] = value;
  };
  keep('fn', tags?.function);
  keep('stage', tags?.stage ?? tags?.failureStage);
  keep('boundary', tags?.boundary);
  return props;
}

function underCap(kind, first, props) {
  const key = `${kind}|${first?.name ?? ''}|${props.fn ?? ''}|${props.stage ?? ''}`;
  const count = (capCounts.get(key) ?? 0) + 1;
  capCounts.set(key, count);
  return count <= POSTHOG_CAP_PER_KEY;
}

export function captureException(...args) {
  const result = Sentry.captureException(...args);
  try {
    const props = codedProps(args[1]);
    if (underCap('exc', args[0], props)) captureError(args[0], props);
  } catch {
    // F6: the PostHog half never affects the caller; Sentry already ran.
  }
  return result;
}

// C3: a warning without a coded fn tag would be a content-free event, so it is not sent.
export function captureMessage(...args) {
  const result = Sentry.captureMessage(...args);
  try {
    const props = codedProps(args[1]);
    if (props.fn && underCap('warn', args[0], props)) track('client_warning', props);
  } catch {
    // F6: as above.
  }
  return result;
}

export function addBreadcrumb(...args) {
  return Sentry.addBreadcrumb(...args);
}

// Reporter injected into the analytics layer (useOnboardingGate, #1416) for its own failures.
// Analytics failures are recoverable, so this is a warning (CC-IMPL-037), and it
// carries only the error name -- never message text. Must never throw: it runs
// inside analytics' catch paths.
export function reportSentryOnly(err) {
  try {
    Sentry.captureMessage(`analytics internal: ${err?.name}`, 'warning');
  } catch {
    // Reporting the reporter's failure has nowhere to go.
  }
}
